import { Link, useInRouterContext } from 'react-router';
import { SCREEN_REQUEST } from '../lib/useAssistPending';

interface Props {
  /** The console route, e.g. `/systems/logs`. */
  to: string;
  /** The fixture preview's name for the same screen, e.g. `logs`. */
  previewTab: string;
  /** Extra query, without the `?`: `level=warn`. Carried to both addresses. */
  search?: string;
  className?: string;
  title?: string;
  children: React.ReactNode;
}

/**
 * A link from one console screen to another.
 *
 * The fixture preview renders screens OUTSIDE any router, and a bare `<Link>`
 * throws there — so a screen with one would take the layout harness down with
 * it. Outside a router this falls back to the preview's own `?tab=` address,
 * which keeps the harness navigable between the screens it shows.
 */
export function ScreenLink({ to, previewTab, search, className, title, children }: Props) {
  const routed = useInRouterContext();
  if (routed) {
    return (
      <Link to={search ? `${to}?${search}` : to} className={className} title={title}>
        {children}
      </Link>
    );
  }
  return (
    <a href={`/preview?tab=${previewTab}${search ? `&${search}` : ''}`} className={className} title={title}>
      {children}
    </a>
  );
}

/**
 * The same, from code: a table row that opens a screen when clicked. Inside
 * the router it asks App's OpenRequestedScreens to navigate (useNavigate
 * throws outside one, and a hook cannot be called only sometimes); in the
 * fixture preview it goes to the preview's address, with `previewSearch` for
 * what the live route carries in its path.
 */
export function useOpenScreen() {
  const routed = useInRouterContext();
  return (to: string, previewTab: string, search?: string, previewSearch?: string) => {
    if (routed) {
      window.dispatchEvent(new CustomEvent(SCREEN_REQUEST, { detail: search ? `${to}?${search}` : to }));
      return;
    }
    const extra = [previewSearch, search].filter(Boolean).join('&');
    window.location.assign(`/preview?tab=${previewTab}${extra ? `&${extra}` : ''}`);
  };
}
